import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import type { NotificationRow } from "@/types/database";
import {
  ArrowLeft, Bell, CheckCheck, ChevronRight,
  ArrowDownLeft, ArrowUpRight, Trophy, Gift, Users, Crown, Megaphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineLoading } from "@/components/common/Loading";
import { useNotifications, useMarkAllRead, useMarkNotificationRead } from "@/hooks/useNotifications";
import { formatRelative, formatFcfa } from "@/lib/format";
import { cn } from "@/lib/utils";

const TEMPLATE_META: Record<string, { icon: typeof Bell; tone: string }> = {
  deposit: { icon: ArrowDownLeft, tone: "bg-success/10 text-success" },
  deposit_failed: { icon: ArrowDownLeft, tone: "bg-destructive/10 text-destructive" },
  withdrawal: { icon: ArrowUpRight, tone: "bg-success/10 text-success" },
  withdrawal_pending: { icon: ArrowUpRight, tone: "bg-warning/10 text-warning" },
  withdrawal_rejected: { icon: ArrowUpRight, tone: "bg-destructive/10 text-destructive" },
  coupon_won: { icon: Trophy, tone: "bg-success/10 text-success" },
  coupon_lost: { icon: Trophy, tone: "bg-white/[0.04] text-muted-foreground" },
  coupon_refunded: { icon: Trophy, tone: "bg-warning/10 text-warning" },
  subscription: { icon: Crown, tone: "bg-primary/10 text-primary" },
  bigboss_upgrade: { icon: Crown, tone: "bg-primary/10 text-primary" },
  referral_purchase: { icon: Users, tone: "bg-primary/10 text-primary" },
  transfer_received: { icon: ArrowDownLeft, tone: "bg-success/10 text-success" },
  transfer_sent: { icon: ArrowUpRight, tone: "bg-white/[0.04] text-muted-foreground" },
  gift_podium: { icon: Gift, tone: "bg-success/10 text-success" },
  gift_elite: { icon: Gift, tone: "bg-primary/10 text-primary" },
  gift_expert: { icon: Gift, tone: "bg-primary/10 text-primary" },
  broadcast: { icon: Megaphone, tone: "bg-white/[0.04] text-muted-foreground" },
};

/** Détermine la page de destination quand on tape sur une notification. */
function deepLink(template: string, payload: Record<string, unknown>): string | null {
  if (payload.couponId) return `/coupon/${String(payload.couponId)}`;
  if (template.startsWith("coupon")) return "/history";
  if (template.startsWith("withdrawal") || template === "deposit" || template.startsWith("transfer"))
    return "/wallet";
  if (template === "subscription" || template === "bigboss_upgrade") return "/plans";
  if (template === "referral_purchase") return "/referral";
  if (template.startsWith("gift")) return "/gift";
  return null;
}

export function Notifications() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading } = useNotifications();
  const markAll = useMarkAllRead();
  const markOne = useMarkNotificationRead();
  const hasUnread = (data ?? []).some((n) => !n.read_at);

  // Dès qu'on arrive sur la page, on marque tout comme lu.
  // Stratégie en deux temps pour garantir un badge à 0 instantané, même si
  // le refetchInterval de useNotifications écrase un optimistic update de
  // la mutation, ou si la mutation rate côté serveur :
  //   1. setQueryData direct → patch le cache TanStack tout de suite,
  //      la badge BottomNav (qui lit le même cache via useUnreadCount)
  //      passe à 0 dans le frame suivant.
  //   2. markAll.mutate() → persiste côté serveur en arrière-plan.
  const didAutoMarkRef = useRef(false);
  useEffect(() => {
    if (isLoading || !hasUnread || didAutoMarkRef.current || !user?.id) return;
    didAutoMarkRef.current = true;

    const now = new Date().toISOString();
    qc.setQueryData<NotificationRow[]>(["notifications", user.id], (old) =>
      (old ?? []).map((n) => (n.read_at ? n : { ...n, read_at: now })),
    );
    markAll.mutate();
  }, [isLoading, hasUnread, user?.id, qc, markAll]);

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm"><Link to="/"><ArrowLeft className="size-[18px]" /></Link></Button>
        <h1 className="text-base font-semibold tracking-tight flex-1 text-center">Alertes</h1>
        {hasUnread ? (
          <Button size="sm" variant="ghost" onClick={() => markAll.mutate()} className="text-primary">
            <CheckCheck className="size-3.5" /> Lu
          </Button>
        ) : <div className="size-8" />}
      </header>

      {isLoading ? (
        <InlineLoading />
      ) : (data?.length ?? 0) === 0 ? (
        <EmptyState icon={Bell} title="Aucune notification" description="Tu seras alerté ici des dépôts, gains et parrainages." />
      ) : (
        <div className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06] overflow-hidden">
          {(data ?? []).map((n) => {
            const payload = (n.payload as Record<string, unknown>) ?? {};
            const formatted: Record<string, string> = {};
            for (const [k, v] of Object.entries(payload)) {
              formatted[k] = k === "amount" && typeof v === "number" ? formatFcfa(v) : String(v);
            }
            const title = t(`notifications.templates.${n.template}`, { defaultValue: n.template });
            const body = t(`notifications.templates.${n.template}_body`, { ...formatted, defaultValue: "" });
            const meta = TEMPLATE_META[n.template] ?? { icon: Bell, tone: "bg-white/[0.04] text-muted-foreground" };
            const Icon = meta.icon;
            const unread = !n.read_at;
            const link = deepLink(n.template, payload);

            function handleClick() {
              if (unread) markOne.mutate(n.id);
              if (link) navigate(link);
            }

            return (
              <button
                key={n.id}
                onClick={handleClick}
                className={cn(
                  "w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors",
                  unread && "bg-primary/[0.03]"
                )}
              >
                <div className={cn("size-8 rounded-md flex items-center justify-center shrink-0", meta.tone)}>
                  <Icon className="size-4" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{title}</p>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {unread && <span className="size-1.5 rounded-full bg-primary" />}
                      {link && <ChevronRight className="size-3.5 text-muted-foreground" />}
                    </div>
                  </div>
                  {body && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{body}</p>}
                  <p className="text-2xs text-muted-foreground mt-1">
                    {formatRelative(n.created_at, i18n.language as "fr" | "en")}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
